import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Database, Server, Play, CheckCircle, AlertTriangle, RefreshCw, List, Users, Clock, PauseCircle, PlayCircle, Trash2, RotateCcw, Settings, Key, Cpu, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './SystemStatus.css';

const SystemStatus = () => {
    const { user } = useAuth();
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [triggering, setTriggering] = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);
    const [queueActionLoading, setQueueActionLoading] = useState(false);
    const [queueActionResult, setQueueActionResult] = useState(null);

    // Gemini settings state
    const [geminiSettings, setGeminiSettings] = useState({
        gemini_api_key: '',
        gemini_model: 'gemini-2.0-flash'
    });
    const [geminiLoading, setGeminiLoading] = useState(false);
    const [geminiSaveResult, setGeminiSaveResult] = useState(null);
    const [apiKeyStatus, setApiKeyStatus] = useState(null); // null, 'checking', 'valid', 'invalid'
    const [testResult, setTestResult] = useState(null);
    const [availableModels, setAvailableModels] = useState([]);

    // AI Proxies state
    const [aiProxies, setAiProxies] = useState([]);
    const [proxyForm, setProxyForm] = useState({ address: '', port: '', username: '', password: '' });
    const [proxyLoading, setProxyLoading] = useState(false);
    const [proxySaveResult, setProxySaveResult] = useState(null);

    // Scheduled checks state
    const [scheduledChecksEnabled, setScheduledChecksEnabled] = useState(true);
    const [scheduledChecksSaving, setScheduledChecksSaving] = useState(false);

    const fetchHealth = async () => {
        try {
            const token = localStorage.getItem('token');
            // Note: health endpoint is public in server.js, but good to have token ready if needed later
            const response = await axios.get('/api/health');
            setHealthData(response.data);
            setError(null);
        } catch (err) {
            console.error('Health check failed:', err);
            // Don't show error on every poll if it fails occasionally, but for now we set it
            // setError('Не удалось получить статус системы');
            // setHealthData(null); 
        } finally {
            setLoading(false);
        }
    };

    const handleQueueAction = async (action) => {
        const messages = {
            stop: 'Остановить обработку очереди?',
            start: 'Запустить обработку очереди?',
            purge: 'ОЧИСТИТЬ очередь? Это удалит все ожидающие задачи и отменит их.'
        };

        if (!window.confirm(messages[action])) return;

        setQueueActionLoading(true);
        setQueueActionResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/admin/queue/${action}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQueueActionResult({ success: true, message: response.data.message });
            fetchHealth();
        } catch (err) {
            setQueueActionResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка выполнения действия'
            });
        } finally {
            setQueueActionLoading(false);
        }
    };

    const handleCleanupStuck = async () => {
        if (!window.confirm('Очистить зависшие проверки (старше 10 минут)?')) return;

        setQueueActionLoading(true);
        setQueueActionResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/admin/cleanup-stuck', { timeoutMinutes: 10 }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQueueActionResult({ success: true, message: response.data.message });
            fetchHealth();
        } catch (err) {
            setQueueActionResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка очистки зависших проверок'
            });
        } finally {
            setQueueActionLoading(false);
        }
    };

    // Fetch Gemini settings
    const fetchGeminiSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/admin/settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGeminiSettings({
                gemini_api_key: response.data.gemini_api_key || '',
                gemini_model: response.data.gemini_model || 'gemini-2.0-flash'
            });
            // Load scheduled checks setting
            setScheduledChecksEnabled(response.data.scheduled_checks_enabled !== false);
            // If API key exists, fetch models
            if (response.data.gemini_api_key) {
                fetchAvailableModels();
            }
        } catch (err) {
            console.error('Failed to fetch Gemini settings:', err);
        }
    };

    // Fetch available models from Gemini
    const fetchAvailableModels = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/admin/gemini/models', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableModels(response.data.models || []);
            if (response.data.valid) {
                setApiKeyStatus('valid');
            }
        } catch (err) {
            console.error('Failed to fetch models:', err);
            // Set default models if fetch fails
            setAvailableModels([
                { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash' },
                { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash-Lite' },
                { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
                { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
                { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash Preview' },
                { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro Preview' }
            ]);
        }
    };

    // Fetch AI Proxies
    const fetchAiProxies = async () => {
        setProxyLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/admin/ai-proxies', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAiProxies(response.data || []);
        } catch (err) {
            console.error('Failed to fetch AI proxies:', err);
        } finally {
            setProxyLoading(false);
        }
    };

    // Test API key
    const handleTestApiKey = async () => {
        setApiKeyStatus('checking');
        setTestResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/admin/gemini/test',
                { api_key: geminiSettings.gemini_api_key },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.valid) {
                setApiKeyStatus('valid');
                setTestResult({ success: true, message: `✓ ${response.data.message}` });
            } else {
                setApiKeyStatus('invalid');
                // Детальное сообщение об ошибке Gemini
                const errorDetails = response.data.error || 'Ключ недействителен';
                const statusCode = response.data.status ? ` (HTTP ${response.data.status})` : '';
                setTestResult({ success: false, message: `Gemini API: ${errorDetails}${statusCode}` });
            }
        } catch (err) {
            setApiKeyStatus('invalid');
            // Подробное логирование ошибки
            const errorMsg = err.response?.data?.error || err.message || 'Ошибка проверки ключа';
            const statusCode = err.response?.status ? ` (HTTP ${err.response.status})` : '';
            const errorDetails = err.response?.data?.details ? ` - ${err.response.data.details}` : '';
            setTestResult({ success: false, message: `Gemini API: ${errorMsg}${statusCode}${errorDetails}` });
        }
    };

    // Add proxy
    const handleAddProxy = async () => {
        if (!proxyForm.address || !proxyForm.port) {
            setProxySaveResult({ success: false, message: 'Адрес и порт обязательны' });
            return;
        }
        setProxyLoading(true);
        setProxySaveResult(null);
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/admin/ai-proxies', proxyForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProxyForm({ address: '', port: '', username: '', password: '' });
            setProxySaveResult({ success: true, message: 'Прокси добавлен!' });
            fetchAiProxies();
        } catch (err) {
            setProxySaveResult({ success: false, message: err.response?.data?.error || 'Ошибка добавления прокси' });
        } finally {
            setProxyLoading(false);
        }
    };

    // Delete proxy
    const handleDeleteProxy = async (id) => {
        if (!window.confirm('Удалить этот прокси?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/admin/ai-proxies/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchAiProxies();
        } catch (err) {
            console.error('Failed to delete proxy:', err);
        }
    };

    // Toggle scheduled checks
    const handleToggleScheduledChecks = async () => {
        setScheduledChecksSaving(true);
        try {
            const token = localStorage.getItem('token');
            const newValue = !scheduledChecksEnabled;
            await axios.post('/api/admin/settings', 
                { scheduled_checks_enabled: newValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduledChecksEnabled(newValue);
        } catch (err) {
            console.error('Failed to toggle scheduled checks:', err);
        } finally {
            setScheduledChecksSaving(false);
        }
    };

    useEffect(() => {
        fetchHealth();
        fetchGeminiSettings();
        fetchAiProxies();
        const interval = setInterval(fetchHealth, 3000);
        return () => clearInterval(interval);
    }, []);

    const handleManualTrigger = async () => {
        if (!window.confirm('Вы уверены, что хотите запустить проверку всех активных запросов вручную?')) {
            return;
        }

        setTriggering(true);
        setTriggerResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/admin/trigger-checks', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTriggerResult({ success: true, message: 'Проверка успешно поставлена в очередь!' });
            // Refresh health to check if anything changed (though queues state isn't in health yet)
            fetchHealth();
        } catch (err) {
            console.error('Trigger failed:', err);
            setTriggerResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка при запуске проверки'
            });
        } finally {
            setTriggering(false);
        }
    };

    const getStatusColor = (status) => {
        return status === 'connected' || status === 'ok' ? 'success' : 'error';
    };

    const getStatusIcon = (status) => {
        return status === 'connected' || status === 'ok' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />;
    };

    const handleGeminiSettingsChange = (e) => {
        const { name, value } = e.target;
        setGeminiSettings((prev) => ({ ...prev, [name]: value }));
    };

    const handleGeminiSave = async () => {
        setGeminiLoading(true);
        setGeminiSaveResult(null);
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/admin/settings', geminiSettings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGeminiSaveResult({ success: true, message: 'Настройки Gemini сохранены!' });
        } catch (err) {
            setGeminiSaveResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка сохранения настроек'
            });
        } finally {
            setGeminiLoading(false);
        }
    };

    if (loading && !healthData) {
        return <div className="system-status-loading">Загрузка статуса системы...</div>;
    }

    return (
        <div className="system-status-page">
            <div className="page-header">
                <h1>
                    <Activity size={28} />
                    <span>Статус системы</span>
                </h1>
                <button className="refresh-btn" onClick={fetchHealth} title="Обновить статус">
                    <RefreshCw size={20} />
                </button>
            </div>

            {error && (
                <div className="error-banner">
                    <AlertTriangle size={24} />
                    <span>{error}</span>
                </div>
            )}

            <div className="status-grid">
                {/* Database Status */}
                <div className={`status-card ${healthData ? getStatusColor(healthData.services.database) : ''}`}>
                    <div className="card-icon">
                        <Database size={32} />
                    </div>
                    <div className="card-info">
                        <h3>База данных MySQL</h3>
                        <div className="status-badge">
                            {healthData ? getStatusIcon(healthData.services.database) : '-'}
                            <span>{healthData?.services.database === 'connected' ? 'Подключено' : 'Ошибка'}</span>
                        </div>
                    </div>
                </div>

                {/* RabbitMQ Status */}
                <div className={`status-card ${healthData ? getStatusColor(healthData.services.rabbitmq) : ''}`}>
                    <div className="card-icon">
                        <Server size={32} />
                    </div>
                    <div className="card-info">
                        <h3>Очередь RabbitMQ</h3>
                        <div className="status-badge">
                            {healthData ? getStatusIcon(healthData.services.rabbitmq) : '-'}
                            <span>{healthData?.services.rabbitmq === 'connected' ? 'Подключено' : 'Ошибка'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {healthData && healthData.metrics && (
                <div className="metrics-section">
                    <h2>Метрики очереди</h2>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className="metric-icon">
                                <List size={24} />
                            </div>
                            <div className="metric-content">
                                <span className="metric-label">Сообщений в очереди</span>
                                <span className="metric-value">{healthData.metrics.rabbitmq_messages}</span>
                            </div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <Users size={24} />
                            </div>
                            <div className="metric-content">
                                <span className="metric-label">Активных воркеров</span>
                                <span className="metric-value">{healthData.metrics.rabbitmq_consumers}</span>
                            </div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <Clock size={24} />
                            </div>
                            <div className="metric-content">
                                <span className="metric-label">Ожидает/В процессе (БД)</span>
                                <span className="metric-value">{healthData.metrics.db_pending_checks}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="actions-section">
                <h2>Управление</h2>
                <div className="actions-grid">
                    <div className="actions-card">
                        <div className="action-info">
                            <h3>Ежедневные проверки (00:00)</h3>
                            <p>Автоматическая проверка всех активных запросов каждый день в полночь.</p>
                        </div>
                        <div className="toggle-switch-container">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={scheduledChecksEnabled}
                                    onChange={handleToggleScheduledChecks}
                                    disabled={scheduledChecksSaving}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className={`toggle-label ${scheduledChecksEnabled ? 'enabled' : 'disabled'}`}>
                                {scheduledChecksSaving ? 'Сохранение...' : (scheduledChecksEnabled ? 'Включено' : 'Выключено')}
                            </span>
                        </div>
                    </div>

                    <div className="actions-card">
                        <div className="action-info">
                            <h3>Принудительная проверка</h3>
                            <p>Запустите проверку всех активных запросов вручную.</p>
                        </div>
                        <button
                            className="trigger-btn"
                            onClick={handleManualTrigger}
                            disabled={triggering || !healthData || healthData.services.rabbitmq !== 'connected'}
                        >
                            {triggering ? <RefreshCw className="spin" size={20} /> : <Play size={20} />}
                            <span>{triggering ? 'Запуск...' : 'Запустить проверку'}</span>
                        </button>
                    </div>

                    <div className="actions-card">
                        <div className="action-info">
                            <h3>Управление очередью</h3>
                            <p>Остановка и очистка очереди задач.</p>
                        </div>
                        <div className="queue-actions">
                            {healthData?.metrics?.rabbitmq_processing_status === 'running' ? (
                                <button
                                    className="action-btn stop"
                                    onClick={() => handleQueueAction('stop')}
                                    disabled={queueActionLoading || !healthData || healthData.services.rabbitmq !== 'connected'}
                                >
                                    {queueActionLoading ? <RefreshCw className="spin" size={20} /> : <PauseCircle size={20} />}
                                    <span>Остановить</span>
                                </button>
                            ) : (
                                <button
                                    className="action-btn start"
                                    onClick={() => handleQueueAction('start')}
                                    disabled={queueActionLoading || !healthData || healthData.services.rabbitmq !== 'connected'}
                                >
                                    {queueActionLoading ? <RefreshCw className="spin" size={20} /> : <PlayCircle size={20} />}
                                    <span>Запустить</span>
                                </button>
                            )}

                            <button
                                className="action-btn purge"
                                onClick={() => handleQueueAction('purge')}
                                title="Удалить все задачи из очереди"
                                disabled={queueActionLoading || !healthData || healthData.services.rabbitmq !== 'connected'}
                            >
                                {queueActionLoading ? <RefreshCw className="spin" size={20} /> : <Trash2 size={20} />}
                                <span>Очистить</span>
                            </button>

                            <button
                                className="action-btn cleanup"
                                onClick={handleCleanupStuck}
                                title="Очистить зависшие проверки (старше 10 минут)"
                                disabled={queueActionLoading}
                            >
                                {queueActionLoading ? <RefreshCw className="spin" size={20} /> : <RotateCcw size={20} />}
                                <span>Зависшие</span>
                            </button>
                        </div>
                    </div>
                </div>

                {triggerResult && (
                    <div className={`trigger-result ${triggerResult.success ? 'success' : 'error'}`}>
                        {triggerResult.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                        <span>{triggerResult.message}</span>
                    </div>
                )}

                {queueActionResult && (
                    <div className={`trigger-result ${queueActionResult.success ? 'success' : 'error'}`}>
                        {queueActionResult.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                        <span>{queueActionResult.message}</span>
                    </div>
                )}
            </div>

            <div className="info-section">
                <h2>Информация о системе</h2>
                <div className="info-grid">
                    <div className="info-item">
                        <span className="label">Node.js Environment:</span>
                        <span className="value">{process.env.NODE_ENV || 'production'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Last Updated:</span>
                        <span className="value">{new Date().toLocaleString()}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Client Version:</span>
                        <span className="value">v1.1.0</span>
                    </div>
                </div>
            </div>

            <div className="openrouter-section">
                <h2>Настройки Gemini AI</h2>
                <div className="openrouter-content">
                    <div className="openrouter-item">
                        <label>API Key:</label>
                        <input
                            type="password"
                            name="gemini_api_key"
                            value={geminiSettings.gemini_api_key}
                            onChange={handleGeminiSettingsChange}
                            disabled={geminiLoading}
                            placeholder="AIza..."
                        />
                        {apiKeyStatus && (
                            <div className={`api-key-status ${apiKeyStatus}`}>
                                {apiKeyStatus === 'checking' && (
                                    <><RefreshCw className="spin" size={14} /> Проверка...</>
                                )}
                                {apiKeyStatus === 'valid' && (
                                    <><CheckCircle size={14} /> Ключ действителен</>
                                )}
                                {apiKeyStatus === 'invalid' && (
                                    <><AlertTriangle size={14} /> Ключ недействителен</>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="openrouter-item">
                        <label>Модель:</label>
                        <select
                            name="gemini_model"
                            value={geminiSettings.gemini_model}
                            onChange={handleGeminiSettingsChange}
                            disabled={geminiLoading}
                        >
                            {availableModels.length > 0 ? (
                                availableModels.map(model => (
                                    <option key={model.id} value={model.id}>
                                        {model.name || model.id}
                                    </option>
                                ))
                            ) : (
                                <>
                                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                                </>
                            )}
                        </select>
                    </div>

                    <div className="openrouter-actions">
                        <button
                            className="test-btn"
                            onClick={handleTestApiKey}
                            disabled={geminiLoading || !geminiSettings.gemini_api_key || apiKeyStatus === 'checking'}
                        >
                            {apiKeyStatus === 'checking' ? <RefreshCw className="spin" size={20} /> : <Key size={20} />}
                            <span>Проверить ключ</span>
                        </button>
                        <button
                            className="save-btn"
                            onClick={handleGeminiSave}
                            disabled={geminiLoading}
                        >
                            {geminiLoading ? <RefreshCw className="spin" size={20} /> : <Save size={20} />}
                            <span>{geminiLoading ? 'Сохранение...' : 'Сохранить настройки'}</span>
                        </button>
                    </div>

                    {testResult && (
                        <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                            {testResult.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                            <span>{testResult.message}</span>
                        </div>
                    )}

                    {geminiSaveResult && (
                        <div className={`save-result ${geminiSaveResult.success ? 'success' : 'error'}`}>
                            {geminiSaveResult.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                            <span>{geminiSaveResult.message}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="openrouter-section">
                <h2>AI Прокси</h2>
                <div className="openrouter-content">
                    <div className="proxy-form">
                        <div className="proxy-form-row">
                            <input
                                type="text"
                                placeholder="Адрес"
                                value={proxyForm.address}
                                onChange={(e) => setProxyForm(prev => ({ ...prev, address: e.target.value }))}
                                disabled={proxyLoading}
                            />
                            <input
                                type="number"
                                placeholder="Порт"
                                value={proxyForm.port}
                                onChange={(e) => setProxyForm(prev => ({ ...prev, port: e.target.value }))}
                                disabled={proxyLoading}
                                style={{ width: '100px' }}
                            />
                            <input
                                type="text"
                                placeholder="Пользователь (опционально)"
                                value={proxyForm.username}
                                onChange={(e) => setProxyForm(prev => ({ ...prev, username: e.target.value }))}
                                disabled={proxyLoading}
                            />
                            <input
                                type="password"
                                placeholder="Пароль (опционально)"
                                value={proxyForm.password}
                                onChange={(e) => setProxyForm(prev => ({ ...prev, password: e.target.value }))}
                                disabled={proxyLoading}
                            />
                            <button
                                className="save-btn"
                                onClick={handleAddProxy}
                                disabled={proxyLoading || !proxyForm.address || !proxyForm.port}
                            >
                                {proxyLoading ? <RefreshCw className="spin" size={20} /> : <span>+</span>}
                            </button>
                        </div>
                    </div>

                    {proxySaveResult && (
                        <div className={`save-result ${proxySaveResult.success ? 'success' : 'error'}`}>
                            {proxySaveResult.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                            <span>{proxySaveResult.message}</span>
                        </div>
                    )}

                    {aiProxies.length > 0 && (
                        <div className="proxy-list">
                            {aiProxies.map(proxy => (
                                <div key={proxy.id} className="proxy-item">
                                    <span className="proxy-address">{proxy.address}:{proxy.port}</span>
                                    {proxy.username && <span className="proxy-user">({proxy.username})</span>}
                                    <button
                                        className="action-btn purge"
                                        onClick={() => handleDeleteProxy(proxy.id)}
                                        title="Удалить прокси"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {aiProxies.length === 0 && (
                        <div className="proxy-empty">
                            Прокси не настроены. AI запросы будут отправляться напрямую.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SystemStatus;
